import { useEffect, useState, useRef } from "react";
import { fetchAgentEvents, getData, postData } from "../utils/apiFunctions";
import { AgentEventsColumnKey, ApiEndpoints, agentEventFilters, agentEventsColumnDetails, formatDate } from "../utils/common";
import { html } from 'lit'
import { useEventListener } from "../utils/customHooks";
import { HtmlEscapeUtils } from "../utils/HtmlEscapeUtils";

const AgentEventsGrid = () => {
    let grid = null;

    const [selectedAgentIds, setSelectedAgentIds] = useState([])
    const [areDifferentEventsSelected, setAreDifferentEventsSelected] = useState(false)

    const renderDialogFooterButtons = () => {
        const footer = document.createElement('div');

        footer.innerHTML = `
          <gz-button id="resolveBtn" type="outline" color="primary">Resolve</gz-button>
          <gz-button id="cancelBtn" type="outline" color="secondary">Cancel</gz-button>
      `;
        return footer;
    }

    const handleResolveEvent = async (actionValue) => {
        let eventHandleOptions = {
            EventHandleInfo: {
                ActionType: actionValue
            }
        }

        var agentEventsToHandle = selectedAgentIds.map((item) => {
            return {
                EventId: item,
                EventOptions: eventHandleOptions
            }
        })

        await postData(ApiEndpoints.AGENT_EVENTS, agentEventsToHandle, true)

        setTimeout(() => {
            document.getElementById('agentEventsGrid').controller.handleGridItemsLoad(true, true);
        }, 5000);
    }

    const createWindow = (wrapper, data) => {
        const windowBody = document.createElement('div')

        const renderActions = (data) => {
            return data.handlingActions.map((item) => {
                let label = item.actionDetails.split(' ')[0]
                return `
                    <gz-radio class='mt-3' value=${item.actionType} label=${label} fontSize="14"></gz-radio>
                    <div class='ml-3'>
                        <gz-text fontSize="14">
                                ${HtmlEscapeUtils.htmlEncode(item.actionDetails)}
                        </gz-text>
                    </div>`
            }).join('')
        }

        windowBody.innerHTML = `
         <div class='ml-5 mt-10'>
             <div class='mb-2'>
                 <gz-title fontSize="16" fontWeight="500">Event Details</gz-title>
             </div>
             ${HtmlEscapeUtils.htmlEncode(data.title)}
             <div class='mt-6'>
                 <gz-title fontSize="16" fontWeight="500">Event description</gz-title>
             </div>
             <div class='mt-1'>
                 <gz-text fontSize="14">
                    ${HtmlEscapeUtils.htmlEncode(data.description)}
                 </gz-text>
             </div>
             <div class='mt-6'>
                 <gz-title fontSize="16" fontWeight="500">Actions</gz-title>
             </div>
             <div class='mt-3'>
                 <gz-form-field id='actionRadioGroup'>
                     <gz-radio-group gz-form-input>
                        <div class='mb-4'>
                            ${renderActions(data)}
                        </div>
                     </gz-radio-group>
                     <gz-input-error></gz-input-error>
                 </gz-form-field>
             </div>
         `;

        const windowFooter = renderDialogFooterButtons();
        const resolveButton = windowFooter.querySelector('#resolveBtn');
        const cancelButton = windowFooter.querySelector('#cancelBtn');

        const actionRadioGroup = windowBody.querySelector('#actionRadioGroup');

        const windowConfigs = {
            headline: 'Resolve Agent Sync Event',
            bodyContent: windowBody,
            footerContent: windowFooter,
            isOpen: true,
            isWindow: true
        };

        const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(windowConfigs, wrapper);

        if (resolveButton) {
            resolveButton.addEventListener('click', async () => {
                resolveButton.showSpinner = true;
                await handleResolveEvent(actionRadioGroup.value)
                dialog.close();
            });
        }

        if (cancelButton) {
            cancelButton.addEventListener('click', () => dialog.close());
        }

        return dialog;
    }

    const defaultViewProps = {
        id: '1234',
        name: `Default Agent Events View`,
        type: 0,
        filters: agentEventFilters,
        displayedColumns: [0, 1, 2, 3],
        pageSize: 20,
        pageNumber: 1,
        compactView: false
    };

    const getDependencies = () => {
        return {
            api: {
                type: 'nirvana',
            },
            translateFn: (text, replacements) => {
                if (!replacements || !Object.keys(replacements).length) {
                    return text;
                }

                const variables = Object.keys(replacements);
                const variablesTexts = variables.map((variable) => '{' + variable + '}');
                const regex = new RegExp(variablesTexts.join('|'), 'g');

                return text.replace(regex, (matched) => {
                    return replacements[matched.slice(1, -1)];
                });
            },
            getTimezone: () => 'UTC',
            getUserLanguage: () => 'en-US',
        };
    }

    class DataProvider {

        dependencies;
        allColumnDetails;
        targetIds;
        defaultViewProps;
        hasPagination;

        constructor(dependencies, allColumnDetails, targetIds, defaultTargetIds, hasPagination) {
            this.dependencies = dependencies;
            this.allColumnDetails = allColumnDetails;
            this.targetIds = targetIds;
            this.defaultTargetIds = defaultTargetIds;
            this.hasPagination = hasPagination;
        }

        async getSummary(request) {
            const totalAgentEvents = await fetchAgentEvents(request.filters);

            return new Promise(resolve => {
                setTimeout(() => {
                    resolve({
                        summary: `${totalAgentEvents.length} items`,
                        numItems: totalAgentEvents.length,
                    });
                }, 300);
            });
        }

        async getRows(request) {
            const agentEvents = await fetchAgentEvents(request.filters, request.pageNumber, request.pageSize);
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve({
                        rawEvents: agentEvents.map((x) => {
                            return {
                                eventId: x["id"],
                                eventType: x["eventType"],
                                eventTypeName: x["eventTypeName"],
                                rmmEndpointName: x["rmmEndpointName"],
                                gravityZoneEndpointName: x["gravityZoneEndpointName"],
                                createdDate: formatDate(x["timeStamp"])
                            }
                        }),
                    });
                }, 300);
            });
        }

        async updateSettings() {
            return {};
        }

        async setFilters() {
            return {};
        }

        shouldDataReloadOnResetView() {
            return true;
        }
    };

    const dependencies = getDependencies();

    const handleEvents = async () => {
        const body = document.getElementById('agentEvents-container');
        const event = await getData(`${ApiEndpoints.AGENT_EVENTS}/${selectedAgentIds[0]}`);
        createWindow(body, event);
    }

    const getColumnRenderers = {
        [AgentEventsColumnKey.EVENT_TYPE]: rowData => {
            return html`<gz-grid-cell-default-renderer .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.eventTypeName)}"></gz-grid-cell-default-renderer>`;
        },
        [AgentEventsColumnKey.SOURCE_NAME]: rowData => {
            return html`<gz-grid-cell-default-renderer .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.rmmEndpointName)}"></gz-grid-cell-default-renderer>`;
        },
        [AgentEventsColumnKey.DESTINATION_NAME]: rowData => {
            return html`<gz-grid-cell-default-renderer .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.gravityZoneEndpointName)}"></gz-grid-cell-default-renderer>`;
        },
        [AgentEventsColumnKey.GENERATED_AT]: rowData => {
            return html`<gz-grid-cell-default-renderer .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.createdDate)}"></gz-grid-cell-default-renderer>`;
        }
    }

    const provider = new DataProvider(dependencies, agentEventsColumnDetails, [], [], true);

    useEffect(() => {
        grid = document.getElementById('agentEventsGrid');

        grid.dataProvider = provider
        grid.titleConfig = JSON.stringify({ title: 'Agent Events' })
        grid.viewId = '1234'
        grid.allColumnDetails = agentEventsColumnDetails
        grid.defaultViewProps = defaultViewProps
        grid.isCompactViewChecked = false
        grid.columns = [0, 1, 2, 3]
        grid.predefinedFiltersKeys = [AgentEventsColumnKey.EVENT_TYPE, AgentEventsColumnKey.SOURCE_NAME, AgentEventsColumnKey.DESTINATION_NAME]
        grid.filters = agentEventFilters
        grid.pageSize = 20
        grid.hasPagination = true
        grid.columnRenderers = getColumnRenderers
        grid.enableRowClickEvent = false;
        grid.propertiesSet = true
        grid.hasSelectableRows = true

        grid.injectDependencies(dependencies);

        grid.addEventListener('selectRows', function (event) {
            let eventTypes = event.detail.selectedItemsData.map((item) => item.eventType)
            let differentEventTypes = eventTypes.filter(function (item, index, self) {
                // It returns the index of the first instance of each value
                return index === self.indexOf(item);
            });

            if (differentEventTypes && differentEventTypes.length > 1) {
                setAreDifferentEventsSelected(true)
            } else {
                setAreDifferentEventsSelected(false)
                setSelectedAgentIds(event.detail.selectedItemsData.map((item) => item.eventId))
            }
            
        })

        grid.start().then().catch(console.error);
    }, []);

    window.nirvana.translator = {
        translateFn: (x) => x
    };

    let handleEventsButtonRef = useRef(null);
    useEventListener({ eventName: 'click', handler: handleEvents, ref: handleEventsButtonRef });

    return (
        <gz-grid-view style={{ "height": "87vh" }}
            id="agentEventsGrid"
            showGlobalActions>

            <div className="header-buttons flex flex-row" slot="grid-header-actions-slot">
                <div className='mr-3'>
                    <gz-button id='sync-agents-button' className="mr-4" ref={handleEventsButtonRef} {...(areDifferentEventsSelected || selectedAgentIds.length === 0) ? {disabled: true} : {}}>Resolve</gz-button>
                </div>
            </div>
        </gz-grid-view>
    )
}

export default AgentEventsGrid;